import {useCallback, useEffect, useState} from "react";
import {Truck} from "lucide-react";
import {
  createAdminProductShipping,
  deleteAdminProductShipping,
  listAdminProductShipping,
  listAdminProducts,
  updateAdminProductShipping,
  type AdminProductShipping
} from "@corporatesclub/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {AdminAddButton, AdminAlert, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs, AdminRowTrailing} from "./ui";

export function ProductShippingView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("produse");
  const [items, setItems] = useState<AdminProductShipping[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [productId, setProductId] = useState(0);
  const [productOpts, setProductOpts] = useState<{id: number; label: string}[]>([]);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminProductShipping({page, pageSize: 20, search: search || undefined})
      .then((r) => { setItems(r.items); setTotalPages(r.totalPages); })
      .catch((e: Error) => setErr(e.message));
  }, [page, search]);
  useEffect(() => { load(); }, [page]);
  useEffect(() => {
    listAdminProducts({page: 1, pageSize: 100}).then((r) => {
      setProductOpts(r.items.map((p) => ({id: p.id, label: `${p.cod} ${p.nume}`})));
    }).catch(() => {});
  }, []);
  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(s: AdminProductShipping) {
    setOpenId(s.id);
    setProductId(s.productId);
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setProductId(0);
    setModalOpen(true);
  }
  async function saveItem() {
    try {
        if(openId === "new") {
          if(!productId) return;
          await createAdminProductShipping({productId});
          adminToast.success("Rule added.");
        } else if(typeof openId === "number") {
          await updateAdminProductShipping(openId, {productId});
          adminToast.success("Rule saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete rule?", async () => {
      await deleteAdminProductShipping(id);
      closeModal(false);
      load();
      adminToast.success("Rule deleted.");
    });
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.produse.title}
      description="Products with free shipping"
      breadcrumbs={[
        {label: ADMIN_SECTION_NAV.produse.title, href: "/vadmin/produse"},
        {label: "Shipping"}
      ]}
      actions={<AdminAddButton onClick={openNew} label="Add rule" />}
    >
      {confirmDialog}
      <AdminSectionTabs section="produse" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(s) => s.id}
        getTitle={(s) => s.productName}
        getSubtitle={(s) => `Product #${s.productId}`}
        getSearchText={(s) => adminSearchText(s.id, s.productId, s.productName, productOpts.map((p) => p.label))}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        emptyTitle="No shipping rules"
        emptyDescription="Add a new rule or adjust filters."
        searchPlaceholder="Search product..."
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={() => { setPage(1); load(); }}
        clientFilter={false}
        getRowActions={(s) => (
          <AdminRowTrailing onDelete={() => removeItem(s.id)} deleteLabel="Delete rule" />
        )}
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New rule" : "Edit rule"}
        icon={Truck}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <div className="admin-detail-panel__form">
          <AdminField label="Product">
            <Select value={String(productId)} onValueChange={(v) => setProductId(Number(v))}>
              <SelectTrigger><SelectValue placeholder="Free-shipping product" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Free-shipping product</SelectItem>
                {productOpts.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </AdminField>
        </div>
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
